import { Schema } from "effect"
import { AccountId, AccountSnapshot, AccountSummary, Fill, Order, Position, PositionMark, Runner } from "../Money.ts"
import { topicFamily } from "./TopicFamily.ts"

// The live view of one paper account: a snapshot, then one Update per change in
// journal order, plus a latest-wins status for what changes on every bar.

/** Subscribe to { _tag: "Account", accountId }. Only the account's owner may. */
export const AccountTopicSchema = Schema.TaggedStruct("Account", { accountId: AccountId })
export type AccountTopicSchema = typeof AccountTopicSchema.Type

export const accountTopicKey = (accountId: AccountId): string => `account:${accountId}`

/** An order was created or changed status (replace it by id). */
export const OrderUpserted = Schema.TaggedStruct("OrderUpserted", { order: Order })
/** A new fill (append; fills never change). */
export const FillAdded = Schema.TaggedStruct("FillAdded", { fill: Fill })
/** A position's quantity, average price or realized P&L changed (replace it by instrument and product). */
export const PositionUpserted = Schema.TaggedStruct("PositionUpserted", { position: Position })
/** A runner was attached or changed status (replace it by id). */
export const RunnerUpserted = Schema.TaggedStruct("RunnerUpserted", { runner: Runner })

export const AccountUpdate = Schema.Union([OrderUpserted, FillAdded, PositionUpserted, RunnerUpserted])
export type AccountUpdate = typeof AccountUpdate.Type

/**
 * Balances, clock and marks: everything that changes on every bar. It replaces
 * the previous status entirely; `marks` overlays the snapshot's positions.
 */
export const SummaryChanged = Schema.TaggedStruct("SummaryChanged", {
  summary: AccountSummary,
  marks: Schema.Array(PositionMark)
})

export const AccountTopicStatus = Schema.Union([SummaryChanged])
export type AccountTopicStatus = typeof AccountTopicStatus.Type

export const AccountTopic = topicFamily({
  tag: "Account",
  topic: AccountTopicSchema,
  key: (topic) => accountTopicKey(topic.accountId),
  snapshot: AccountSnapshot,
  update: AccountUpdate,
  status: AccountTopicStatus
})
